import { MdVideocamOff } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import 'twin.macro';

/**
 * Shown in place of the camera column when the camera exists but cannot be
 * opened — another app or tab is holding it (CAMERA_ERROR_CODE.CAMERA_IN_USE).
 *
 * Replaces the old full-bleed frame, which carried a 48px headline, a "refresh
 * the page" line, and a hover-only icon that hid the actual advice behind a
 * tooltip. This says what is wrong, what to do about it, and offers the retry
 * directly — no page reload, since the camera can be released by the other app
 * at any moment.
 *
 * A fixed 380px card rather than a `w-full` one: the camera column is sized by
 * its contents, and with no <video> and no device picker in this state there is
 * nothing else left in it to give it a width. A definite width is what the
 * column measures, so the card can't collapse to the width of its own longest
 * word. `max-w-full` hands the viewport back the decision on a narrow phone.
 */
const CameraUnavailablePanel = ({ onRetry, onContinueWithoutCamera, isRetrying = false }) => {
  const { t } = useTranslation('camera-permission');

  return (
    <div tw="flex flex-col items-center gap-[12px] px-[20px] py-[24px] w-[380px] max-w-full text-center bg-white rounded-[12px] border border-[#D9DFE4]">
      <div tw="flex justify-center items-center bg-[#FDECEC] rounded-full shrink-0 size-[48px]">
        <MdVideocamOff size={26} tw="text-[#D93A3A]" />
      </div>

      <p tw="text-[18px] font-bold text-[#2E2E2E] leading-[1.3]">{t('tooltip.title')}</p>

      {/* The two checks that actually free the camera — previously buried in a
          tooltip behind a hover-only icon. */}
      <ul tw="flex flex-col gap-[8px] w-full text-[13px] text-[#656565] leading-[1.45] text-start">
        <li tw="flex gap-[8px]">
          <span aria-hidden="true">•</span>
          <span>{t('tooltip.checkOtherApps')}</span>
        </li>
        <li tw="flex gap-[8px]">
          <span aria-hidden="true">•</span>
          <span>{t('tooltip.checkSwitch')}</span>
        </li>
      </ul>

      <button
        type="button"
        onClick={onRetry}
        disabled={isRetrying}
        tw="px-[20px] py-[10px] mt-[4px] w-full text-[14px] font-semibold text-white bg-[#2E2E2E] rounded-[8px] cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isRetrying ? t('unavailablePanel.retrying') : t('unavailablePanel.retry')}
      </button>

      {/* The session must stay startable without a camera — nothing here can
          guarantee the other app ever releases it. Secondary styling: retrying
          is the outcome worth trying first. */}
      <button
        type="button"
        onClick={onContinueWithoutCamera}
        tw="text-[13px] font-medium text-[#656565] underline cursor-pointer hover:text-[#2E2E2E]"
      >
        {t('contentSide.continueWithoutCamera')}
      </button>
    </div>
  );
};

export default CameraUnavailablePanel;
